import { Link } from "react-router-dom";
import { Eye, ShoppingBag, ShieldCheck } from "lucide-react";
import ListedAgo from "@/components/ListedAgo";
import { TID } from "@/constants/tid";
import { fmtPrice } from "@/lib/currency";
import { useI18n } from "@/i18n/I18nContext";

const FALLBACK_IMG = "https://images.unsplash.com/photo-1615788189819-bee84874da4b?auto=format&fit=crop&w=800&q=70";

export default function ProductCard({ product, size = "md" }) {
  const { t } = useI18n();
  const img = product.image_url || FALLBACK_IMG;
  return (
    <Link
      to={`/product/${product.id}`}
      data-testid={TID.productCard}
      className={`group block bg-[#121212] border border-[#27272A] hover:border-[#3f3f46] transition-colors duration-200 rounded-lg overflow-hidden hover:-translate-y-1 transform-gpu ${size === "lg" ? "row-span-2" : ""}`}
      style={{ transitionProperty: "border-color, transform" }}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-[#0A0A0A]">
        <img
          src={img}
          alt={product.title}
          className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-transform duration-500"
          onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
        />
        <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-black/70 backdrop-blur border border-white/10 text-[10px] uppercase tracking-widest text-neutral-300">
          {product.category}
        </div>
        {product.founding_seller && (
          <div data-testid="founding-seller-card-badge" className="absolute bottom-2 left-2 px-2 py-0.5 rounded-full bg-[#FF9F0A]/20 backdrop-blur border border-[#FF9F0A]/50 text-[9px] uppercase tracking-widest text-[#FF9F0A] font-bold">
            ★ Founding seller
          </div>
        )}
        <div className="absolute top-2 right-2">
          <ListedAgo publishedAt={product.published_at} small />
        </div>
      </div>
      <div className="p-4">
        <h3 data-testid={TID.productCardTitle} className="font-bold tracking-tight text-white line-clamp-1">{product.title}</h3>
        <p className="text-neutral-400 text-xs mt-1 line-clamp-2">{product.ai_summary || product.description}</p>
        <div className="mt-3 flex items-center justify-between">
          <div data-testid={TID.productCardPrice} className="font-mono tabular-nums text-white font-bold text-lg">
            {fmtPrice(product.price, product.currency)}
          </div>
          <div className="flex items-center gap-3 text-neutral-500 text-xs font-mono">
            <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" /> {product.views}</span>
            <span className="inline-flex items-center gap-1"><ShoppingBag className="h-3 w-3" /> {product.purchases}</span>
          </div>
        </div>
        <div data-testid="protection-badge" className="mt-2.5 inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold text-emerald-400/90">
          <ShieldCheck className="h-3 w-3" /> {t("bp_badge", "Protected until delivery")}
        </div>
      </div>
    </Link>
  );
}
